import { useRef } from 'react';
import Container from 'components/ui/Container';
import { AiFillBank, AiOutlineNumber, AiOutlineFileDone } from 'react-icons/ai';
import { GoLocation } from 'react-icons/go';
import { FiPhoneCall, FiEdit3 } from 'react-icons/fi';
import { BsBagX } from 'react-icons/bs';
import { MdDriveFileRenameOutline } from 'react-icons/md';
import { BiEnvelope, BiUserCircle } from 'react-icons/bi';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchUserProfileQuery, IProfileDetail } from 'redux/reducers/administrator';
import ReactToPrint from 'react-to-print';
import PrimaryButton from 'components/ui/PrimaryButton';
import { backendURL } from 'redux/reducers/settings';
import { useFetchUserProfile } from 'service/user.query';

const ExternalProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: UserProfile } = useFetchUserProfile(id ?? "", !!id);
  const ref = useRef<HTMLDivElement | null>(null);

  // Icon styles for sidebar
  const iconStyle = { fontSize: '1.2rem', marginRight: '10px', color: '#405189' };

  return (
    <Container title="User Profile">
      {/* Header Section */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h3 className="fw-bold mb-0">External User Profile</h3>
          <p className="text-muted">Manage and view detailed information</p>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate(`/external-user-management/external/${id}`)}
        >
          <FiEdit3 /> Edit Profile
        </button>
      </div>

      <div ref={ref} className="row g-4">
        {/* Left Sidebar: Photo & Contact & Bank */}
        <div className="col-lg-4">
          {/* Profile Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-body text-center p-4">
              <img
                src={backendURL + UserProfile?.image}
                alt="Profile"
                className="rounded-circle img-thumbnail mb-3"
                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
              />
              <h4 className="fw-bold mb-1">
                {UserProfile?.first_name} {UserProfile?.last_name}
              </h4>
              <p className="badge bg-soft-primary text-primary mb-3">
                {UserProfile?.current_position || 'Professional'}
              </p>

              <div className="text-start mt-4">
                <div className="d-flex align-items-center mb-3">
                  <BiEnvelope style={iconStyle} />
                  <div>
                    <small className="text-muted d-block">Email Address</small>
                    <span className="fw-medium">{UserProfile?.email}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <FiPhoneCall style={iconStyle} />
                  <div>
                    <small className="text-muted d-block">Mobile Number</small>
                    <span className="fw-medium">{UserProfile?.mobile_number}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-0">
                  <BiUserCircle style={iconStyle} />
                  <div>
                    <small className="text-muted d-block">PAN Number</small>
                    <span className="fw-medium text-uppercase">{UserProfile?.pan_number}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Details Card */}
          <div className="card shadow-sm">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0 d-flex align-items-center gap-2">
                <AiFillBank className="text-primary" /> Bank Details
              </h6>
            </div>
            <div className="card-body p-0">
              <div className="p-3 border-bottom last-child-border-0">
                <ul className="list-group list-group-flush">
                  <li className="list-group-item d-flex justify-content-between align-items-start px-0 bg-transparent">
                    <div className="ms-2 me-auto text-start">
                      <div className="text-muted small">Account Name</div>
                      <div className="fw-bold">{UserProfile?.bank_account_holder}</div>
                    </div>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-start px-0 bg-transparent">
                    <div className="ms-2 me-auto text-start">
                      <div className="text-muted small">Account Number</div>
                      <div className="fw-bold text-primary">{UserProfile?.bank_account_number}</div>
                    </div>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-start px-0 bg-transparent">
                    <div className="ms-2 me-auto text-start">
                      <div className="text-muted small">Bank & Branch</div>
                      <div className="fw-medium">{UserProfile?.bank_name}</div>
                      <div className="small text-muted">{UserProfile?.bank_branch}</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content: Bio & Experience */}
        <div className="col-lg-8">
          <div className="card shadow-sm h-100">
            <div className="card-body p-4 text-start">
              {/* Bio Section */}
              <section className="mb-5">
                <h5 className="text-primary fw-bold border-bottom pb-2 mb-3">Professional Bio</h5>
                <div
                  className="text-secondary lh-lg"
                  dangerouslySetInnerHTML={{ __html: UserProfile?.bio }}
                />
              </section>

              {/* Experience Section */}
              <section className="mb-5">
                <h5 className="text-primary fw-bold border-bottom pb-2 mb-3">Area of Experience</h5>
                <div
                  className="custom-list-style"
                  dangerouslySetInnerHTML={{ __html: UserProfile?.experience }}
                />
              </section>

              {/* Expertise Section */}
              <section className="mb-5">
                <h5 className="text-primary fw-bold border-bottom pb-2 mb-3">Expertise</h5>
                <div
                  className="custom-list-style text-dark"
                  dangerouslySetInnerHTML={{ __html: UserProfile?.area_of_expertise }}
                />
              </section>

              {/* Document Section */}
              <section>
                <h5 className="text-primary fw-bold border-bottom pb-2 mb-3">Attached Documents</h5>
                <div className="d-flex flex-wrap gap-3">
                  {UserProfile?.user_document?.map((doc: any, index: number) => (
                    <a
                      key={index}
                      href={backendURL + doc?.document}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-none border rounded p-3 text-center hover-shadow transition"
                      style={{ minWidth: '120px', background: '#f8f9fa' }}
                    >
                      <AiOutlineFileDone className="text-danger mb-2" style={{ fontSize: '2.5rem' }} />
                      <div className="small text-dark fw-medium">View File {index + 1}</div>
                    </a>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Print Action */}
      <div className="mt-4 pb-5">
        <ReactToPrint
          content={() => ref.current}
          trigger={() => (
            <div className="d-flex justify-content-end">
              <PrimaryButton title="Print Full Profile" type="button" />
            </div>
          )}
        />
      </div>

      <style>{`
        .bg-soft-primary { background-color: rgba(64, 81, 137, 0.1); }
        .last-child-border-0:last-child { border-bottom: 0 !important; }
        .hover-shadow:hover { box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; transform: translateY(-2px); }
        .transition { transition: all 0.3s ease; }
        .custom-list-style ul { padding-left: 1.2rem; }
        .custom-list-style li { margin-bottom: 0.5rem; }
      `}</style>
    </Container>
  );
};

export default ExternalProfileDetail;